import BaseModel from "./BaseModel";
import BaseObject, { enumCompareStatus } from "./BaseObject";

import Logger from "../Logger";
import ObjectFactory from "./ObjectFactory";
import { sortArrayByProperty } from "./utils";
import { type State } from "../State";
import { isDevelopmentEnvironment } from "../estada-config";

const logger = new Logger();

/**
 * Array-backed storage with the small Map-compatible surface historically
 * exposed as `BaseModelMap.items`. Keys are resolved from the current object,
 * so changing an object's generated ID cannot leave a stale lookup index.
 */
class BaseModelArrayStorage<T extends BaseModel> implements Iterable<[string, T]> {
  private orderedItems: T[] = [];

  get size(): number {
    return this.orderedItems.length;
  }

  get length(): number {
    return this.orderedItems.length;
  }

  get(key: string): T | undefined {
    for (const item of this.orderedItems) {
      if (item.key === key) return item;
    }
    return undefined;
  }

  has(key: string): boolean {
    return this.get(key) !== undefined;
  }

  set(key: string, value: T): this {
    for (let index = 0; index < this.orderedItems.length; index += 1) {
      if (this.orderedItems[index].key !== key) continue;
      this.orderedItems[index] = value;
      return this;
    }
    this.orderedItems.push(value);
    return this;
  }

  delete(key: string): boolean {
    for (let index = 0; index < this.orderedItems.length; index += 1) {
      if (this.orderedItems[index].key !== key) continue;
      this.orderedItems.splice(index, 1);
      return true;
    }
    return false;
  }

  clear(): void {
    this.orderedItems.length = 0;
  }

  *values(): IterableIterator<T> {
    for (const item of this.orderedItems) yield item;
  }

  *keys(): IterableIterator<string> {
    for (const item of this.orderedItems) yield item.key;
  }

  *entries(): IterableIterator<[string, T]> {
    for (const item of this.orderedItems) yield [item.key, item];
  }

  [Symbol.iterator](): IterableIterator<[string, T]> {
    return this.entries();
  }

  forEach(callback: (value: T, key: string, storage: BaseModelArrayStorage<T>) => void): void {
    for (const item of this.orderedItems) callback(item, item.key, this);
  }

  at(index: number): T | undefined {
    const resolvedIndex = index < 0 ? this.orderedItems.length + index : index;
    return this.orderedItems[resolvedIndex];
  }

  indexOf(value: T): number {
    return this.orderedItems.indexOf(value);
  }

  insert(index: number, value: T): void {
    const boundedIndex = Math.max(0, Math.min(index, this.orderedItems.length));
    this.orderedItems.splice(boundedIndex, 0, value);
  }

  move(fromIndex: number, toIndex: number): boolean {
    if (fromIndex < 0 || fromIndex >= this.orderedItems.length) return false;
    const boundedTarget = Math.max(0, Math.min(toIndex, this.orderedItems.length - 1));
    if (fromIndex === boundedTarget) return false;
    const item = this.orderedItems.splice(fromIndex, 1)[0];
    this.orderedItems.splice(boundedTarget, 0, item);
    return true;
  }

  replace(values: Iterable<T>): void {
    this.orderedItems = [];
    for (const value of values) this.orderedItems.push(value);
  }

  toArray(): T[] {
    return [...this.orderedItems];
  }
}

/** Accepts the historical plain array and the former wrapped clipboard shape. */
function collectionItemsFromJSON(data: unknown): unknown[] {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  const wrappedItems = (data as { items?: unknown }).items;
  return Array.isArray(wrappedItems) ? wrappedItems : [];
}

/** Restores explicitly persisted array order while preserving legacy load order. */
function orderPersistentCollectionData(collectionData: any[]): any[] {
  const ordered = [...collectionData];
  const hasCompleteOrder = ordered.every((item) => Number.isInteger(item?.collectionIndex));
  if (!hasCompleteOrder) return ordered;
  ordered.sort((left, right) => left.collectionIndex - right.collectionIndex);
  return ordered;
}

export default class BaseModelMap<T extends BaseModel> extends BaseModel {
  private static beforeAddCallback?: (collection: BaseModelMap<BaseModel>, object: BaseModel, index: number) => void;
  private static beforeRemoveCallback?: (collection: BaseModelMap<BaseModel>, object: BaseModel, index: number) => void;

  static setBeforeAddCallback(
    callback?: (collection: BaseModelMap<BaseModel>, object: BaseModel, index: number) => void,
  ) {
    BaseModelMap.beforeAddCallback = callback;
  }

  static setBeforeRemoveCallback(
    callback?: (collection: BaseModelMap<BaseModel>, object: BaseModel, index: number) => void,
  ) {
    BaseModelMap.beforeRemoveCallback = callback;
  }

  public get className(): string {
    return "BaseModelMap"; // no itx
  }

  get isBaseModelMap(): boolean {
    return true;
  }

  validateImportExport() {
    throw new Error("Method not implemented."); // no itx
  }
  createNewItem(): T {
    return new this.itemClassCreator();
  }

  get size() {
    return this.items.size;
  }
  constructor(
    private itemClassCreator: new () => T,
    parent: BaseModel | undefined,
    subId: string | undefined,
    isPersistent: boolean = true,
  ) {
    super({ storeInObjectRegistry: false }, parent);
    this.propertyId = subId || "";
    this.isPersistent = isPersistent;

    if (parent) {
      this.id = parent.id + "/" + (subId ? subId : "");
    }
  }

  // public class properties

  items = new BaseModelArrayStorage<T>();
  deletedItems?: Map<string, T>;

  //////////////////////////////////////////////////////////////////////////////////////
  //
  // Currently only used inside addressGenaration
  //

  lastItems?: Map<string, T>;

  initLastItems() {
    this.lastItems = new Map<string, T>();
    this.items.forEach((item) => {
      this.lastItems?.set(item.key, item);
    });
  }

  clearLastItems() {
    delete this.lastItems;
  }

  removeNewItemsFromDeletedItems() {
    if (this.deletedItems) {
      for (const [key, deletedItem] of this.deletedItems) {
        if (deletedItem.isNew) {
          this.deletedItems.delete(key);
        }
      }
    }
  }

  //////////////////////////////////////////////////////////////////////////////////////

  newInOtherProjectItems?: Map<string, T>;

  // save & load

  compareTo(other: BaseModelMap<T>): string[] {
    return Array.from(this.items.entries()).flatMap((map) => {
      const thisItem = this.items.get(map[0]);
      const otherItem = other.items.get(map[0]);

      if (thisItem && otherItem) {
        return thisItem.compareTo(otherItem);
      }
      return [`${map[0]} different`];
    });
  }

  fromJSON(data: any, parent: BaseModel | undefined): void {
    const sourceItems = collectionItemsFromJSON(data);
    this.items.clear();
    for (const item of sourceItems) {
      const obj = this.createNewItem();
      obj.parent = this.parent;
      obj.fromJSON(item, parent);
      this.items.set(obj.key, obj); // dont use add, avoid onChange-Event
    }
  }

  toJSON(): any {
    const result: unknown[] = [];
    for (const item of this.items.values()) result.push(item.toJSON());
    return result;
  }

  toPersistentData(): any {
    return undefined;
  }

  async saveToEstadaStorage(
    parentCollection: BaseModelMap<BaseModel> | undefined,
    saveItem: (item: BaseModel) => Promise<boolean>,
    parentObject?: BaseModel, // for object with a single reference (room.page)
  ): Promise<any> {
    if (
      this.parent == null &&
      parentObject == null // "tick!?" für copy to clipboard
    ) {
      const firstItem = this.asArray()[0];
      logger.error(
        `save collection without parent ${this.tag} ${this.name} firstItem: ${firstItem.tag} ${firstItem.name} `,
        this,
      );
      return;
    }

    for (const item of this.items.values()) {
      try {
        await item.saveToEstadaStorage(this, saveItem, parentObject);
      } catch (ex) {
        logger.error(`Error in BaseModelMap.saveToEstadaStorage ${(ex as any).message}`, ex);
      }
    }
    return true;
  }

  async loadFromEstadaStorage(
    data: any,
    parent: BaseModel | undefined,
    loadData: (id: string | undefined, parentId: string | undefined) => Promise<any>,
  ): Promise<boolean> {
    // const data = loadItem(this.id, undefined)

    this.fromPersistentData(data);

    const loadedCollectionData = await loadData(undefined, this.id);
    let collectionDatas = loadedCollectionData;
    if (Array.isArray(loadedCollectionData)) {
      collectionDatas = orderPersistentCollectionData(loadedCollectionData);
    }
    // logger.log(
    //   `loadFromEstadaStorage MAP ${this.id} #of items: ${collectionDatas.length}`,
    // );

    if (collectionDatas) {
      for (let i = 0; i < collectionDatas.length; i++) {
        const itemData = collectionDatas[i];
        var obj = ObjectFactory.createObject(itemData.className);
        if (obj == undefined) {
          obj = this.createNewItem();
        }
        obj.parent = parent;

        await obj.loadFromEstadaStorage(itemData, parent, loadData);

        this.addWithoutUpdate(obj as T);
      }
    }

    return true;
  }

  //////////////////////////////////////////////////////

  private xRaiseOnchange(obj: T) {
    this.raiseOnChange();

    if (this.parent) {
      this.parent.raiseOnChange("childCollection");
    }
  }

  assertRequestChannel(obj) {
    if (this.propertyId == "ch") {
      if (!(obj as any).isRequestChannel) {
        logger.error("assertRequestChannel failed");
      }
    }
    if (this.propertyId == "kc") {
      if ((obj as any).isRequestChannel) {
        logger.error("assertRequestChannel failed");
      }
    }
  }

  assertButtonChannel(obj) {
    // we now have  buttons with actorChannels so this check is not valid anymore
    // if (obj.className == "Channel" && !(this.parent as BaseModel)?.isActor) {
    //   logger.error("assertButtonChannel failed, wrong parent class");
    // }
    if (obj.className == "ButtonChannel" && !(this.parent as BaseModel)?.isButton) {
      logger.error("assertButtonChannel failed, wrong parent class");
    }
  }

  add(obj: T) {
    // obj.propertyId = undefined; // any object (Page!!) i a map is NOT a property//->06.10.23 propertyID of Page is set in setter of
    BaseModelMap.beforeAddCallback?.(this as unknown as BaseModelMap<BaseModel>, obj, this.items.size);
    this.addWithoutUpdate(obj);
    this.xRaiseOnchange(obj);
  }

  addWithoutUpdate(obj: T) {
    if (isDevelopmentEnvironment()) {
      this.assertRequestChannel(obj);

      this.assertButtonChannel(obj);
    }

    // if (!state.isLoadingFromEstadaStorage) {
    //   if (obj.parent != this.parent && obj.parent != this) {
    //     obj.logConsistencyError("invalid parent when adding to BaseModelMap");
    //     if (this.parent?.className == "Root") {
    //       obj.parent = this;
    //     }
    //     else {
    //       obj.parent = this.parent;
    //     }
    //   }
    // }

    //  obj.propertyId = undefined; // any object (Page!!) i a map is NOT a property//->06.10.23 propertyID of Page is set in setter of
    this.items.set(obj.key, obj);
    if (this.monitorForUndo) obj.setMonitorForUndo(true);
  }

  /** Returns the item at its stable persisted collection position. */
  at(index: number): T | undefined {
    return this.items.at(index);
  }

  indexOf(obj: T): number {
    return this.items.indexOf(obj);
  }

  /** Inserts an object at a stable position and records a normal collection add. */
  insert(index: number, obj: T): void {
    if (this.items.has(obj.key)) this.remove(obj);
    const boundedIndex = Math.max(0, Math.min(index, this.items.size));
    BaseModelMap.beforeAddCallback?.(this as unknown as BaseModelMap<BaseModel>, obj, boundedIndex);
    this.items.insert(boundedIndex, obj);
    if (this.monitorForUndo) obj.setMonitorForUndo(true);
    this.xRaiseOnchange(obj);
  }

  /** Moves an existing object while preserving its identity and collection ownership. */
  move(fromIndex: number, toIndex: number): boolean {
    const obj = this.items.at(fromIndex);
    if (!obj || fromIndex === toIndex) return false;
    BaseModelMap.beforeRemoveCallback?.(this as unknown as BaseModelMap<BaseModel>, obj, fromIndex);
    const moved = this.items.move(fromIndex, toIndex);
    if (!moved) return false;
    const newIndex = this.items.indexOf(obj);
    BaseModelMap.beforeAddCallback?.(this as unknown as BaseModelMap<BaseModel>, obj, newIndex);
    this.xRaiseOnchange(obj);
    return true;
  }

  /** Replaces collection contents in the supplied order without emitting change hooks. */
  replaceItems(items: Iterable<T>): void {
    this.items.replace(items);
    if (!this.monitorForUndo) return;
    for (const item of this.items.values()) item.setMonitorForUndo(true);
  }

  override setMonitorForUndo(value: boolean, visited = new Set<BaseObject>()) {
    if (visited.has(this)) return;
    super.setMonitorForUndo(value, visited);
    this.items.forEach((item) => item.setMonitorForUndo(value, visited));
  }

  addArrayWithoutUpdate(items: Array<T>) {
    items.forEach((item) => this.addWithoutUpdate(item));
  }

  addBaseModelMapWithoutUpdate(items: BaseModelMap<T>) {
    items.forEach((item) => this.addWithoutUpdate(item));
  }

  push(obj: T) {
    BaseModelMap.beforeAddCallback?.(this as unknown as BaseModelMap<BaseModel>, obj, this.items.size);
    this.items.set(obj.key, obj);
    if (this.monitorForUndo) obj.setMonitorForUndo(true);
    this.xRaiseOnchange(obj);
  }

  clearDeletedChildCollections() {
    delete this.deletedItems;
  }

  addToDeletedItems(obj: T) {
    if (!this.deletedItems) {
      this.deletedItems = new Map<string, T>();
    }

    obj.isDeleted = true;
    this.deletedItems.set(obj.key, obj);
  }

  remove(obj: T, withoutUpdate?: boolean) {
    if (isDevelopmentEnvironment()) {
      if (!this.items.has(obj.key)) {
        logger.error("remove failed, object not found in BaseModelMap", obj);
      }
    }

    if (!withoutUpdate) {
      const index = Array.from(this.items.values()).indexOf(obj);
      BaseModelMap.beforeRemoveCallback?.(this as unknown as BaseModelMap<BaseModel>, obj, index);
    }

    if (!withoutUpdate && !obj.isNew) {
      if (BaseModel.monitorDelete && (obj.isDeleted || obj.isDeleting)) {
        this.addToDeletedItems(obj);
        if (this.parent) {
          this.parent.dirtyChilds = true;
        }
      }
    }

    this.items.delete(obj.key);

    this.invalidatParentErrorStatus();

    if (!withoutUpdate) {
      this.xRaiseOnchange(obj);
    }
  }

  removeWithoutUpdate(obj: T) {
    this.remove(obj, true);
    this.invalidatParentErrorStatus();
  }

  get = (id: string): T | undefined => {
    return this.items.get(id);
  };

  forEach(callbackfn: (obj: T) => void): void {
    this.items.forEach((obj) => {
      callbackfn(obj as T);
    });
  }

  // Ermöglicht direkte Iteration über BaseModelMap (z.B. for...of)
  [Symbol.iterator](): IterableIterator<T> {
    return this.items.values();
  }

  flatMap<S>(callbackfn: (obj: T) => S[]): S[] {
    const out: S[] = [];
    this.items.forEach((obj) => {
      out.push(...callbackfn(obj as T));
    });
    new Array();
    return out;
  }

  deleteAllItems(): Array<BaseObject> {
    var res = new Array<BaseObject>();
    // delete all child rooms
    const itemsToDelete = this.asArray();
    itemsToDelete.forEach((item) => {
      res = res.concat(item.delete());
    });
    return res;
  }

  get values(): IterableIterator<T> {
    return this.items.values();
  }

  filter(filterFn: (obj: T) => boolean): Array<T> {
    return this.asArray().filter(filterFn);
  }

  asArray(): Array<T> {
    return this.items.toArray();
    // const a = Array<T>();
    // this.items.forEach((element) => {
    //   a.push(element as T);
    // });
    // return a;
  }

  first(): T | undefined {
    return this.items.at(0);
  }

  findAsArray(filter: (item: T) => boolean): Array<T> {
    var res = new Array<T>();

    this.items.forEach((item) => {
      if (filter(item)) {
        res.push(item);
      }
    });

    return res;
  }

  sortBySensytive = (propertyName: string): Array<T> => {
    const array = this.asArray();

    array.sort((a: any, b: any) => {
      var nameA = String(a[propertyName] ?? "").toUpperCase(); // ignore upper and lowercase
      var nameB = String(b[propertyName] ?? "").toUpperCase(); // ignore upper and lowercase

      if (nameA < nameB) {
        return -1;
      }

      if (nameA > nameB) {
        return 1;
      }

      // names must be equal
      return 0;
    });

    return array;
  };

  sortBy = (propertyName: string): Array<T> => {
    return sortArrayByProperty<T>(this.asArray(), propertyName);
  };

  sortBy2 = (propertyName: string): Array<T> => {
    const array = this.asArray();

    array.sort((a: any, b: any) => {
      // Imported properties may contain numbers, booleans or null.
      var nameA = String(a[propertyName] ?? "").toUpperCase();
      var nameB = String(b[propertyName] ?? "").toUpperCase();

      //
      // sort A8 and A10 korrekt ..... ( dont sort S1(2) and S1(10)  )
      //
      if (/[0-9]/.test(nameA) && /[0-9]/.test(nameB)) {
        var numberA_match = nameA.match(/\d+$/);
        var numberB_match = nameB.match(/\d+$/);

        if (numberA_match && numberB_match) {
          var numberA = Number(numberA_match[0]);
          var numberB = Number(numberB_match[0]);

          nameA = nameA.replace(numberA_match[0], "");
          nameB = nameB.replace(numberB_match[0], "");

          if (nameA == nameB) {
            if (numberA < numberB) {
              return -1;
            } else if (numberA > numberB) {
              return 1;
            }

            return 0;
          }
        }
      }
      //
      // End of advanced number sorting

      if (nameA < nameB) {
        return -1;
      }

      if (nameA > nameB) {
        return 1;
      }

      // names must be equal
      return 0;
    });

    return array;
  };

  sortIntBy = (propertyName: string): Array<T> => {
    const array = this.asArray();

    array.sort((a: any, b: any) => {
      const nameA = parseInt(a[propertyName]);
      const nameB = parseInt(b[propertyName]);

      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      // names must be equal
      return 0;
    });

    return array;
  };

  ////////////////////////////////////////////////////////////////////////////////////////

  checkDirtyChilds() {
    let res = false;

    for (const [key, obj] of this.items) {
      if (obj.isDirty || obj.dirtyChilds) {
        res = true;
        break;
      }
    }

    if (res) {
      this._dirtyChilds = true;
    } else {
      delete this._dirtyChilds;
    }
  }

  findAllErrorsInChildErrorsInPropertyObjects(): Array<BaseModel> {
    let res = new Array<BaseModel>();

    this.items.forEach((item) => {
      const res1 = item.findAllErrorInChildObjects();
      res = res.concat(res1);
    });

    return res;
  }

  cumulateChildObjectsErrorStatus(): boolean {
    for (const entry of this.items) {
      const item = entry[1];
      // Check the item's own errors first, then recurse into its children.
      if (item.hasErrors || item.cumulateChildObjectsErrorStatus()) {
        return true;
      }
    }
    return false;
  }

  getSelectedItem(): BaseModel | undefined {
    let selectedItem: BaseModel | undefined = undefined;
    this.items.forEach((item) => {
      if (item.selected) {
        selectedItem = item;
      }
    });
    return selectedItem;
  }

  //
  // append another map to this
  //
  append(map: BaseModelMap<T>) {
    map.items.forEach((value, key) => {
      this.items.set(key, value);
      if (this.monitorForUndo) value.setMonitorForUndo(true);
    });
  }

  removeIf(ifFunc: (ga: any) => boolean) {
    const toRemove = new Array<T>();
    this.items.forEach((obj) => {
      // eslint-disable-line
      if (ifFunc(obj)) {
        toRemove.push(obj);
      }
    });

    toRemove.forEach((obj) => {
      this.remove(obj);
    });
  }

  clear() {
    if (this.parent) {
      if (this.propertyId != "") {
        this.id = this.parent.id + "/" + this.propertyId;
      }
    }

    this._selected = false;

    if (this.items.size != 0) {
      const currentItems = Array.from(this.items.values());
      currentItems.forEach((item, index) =>
        BaseModelMap.beforeRemoveCallback?.(this as unknown as BaseModelMap<BaseModel>, item, index),
      );
      this.items.clear();
      this.raiseOnChange();
      if (this.parent?.className == "Root") {
        this.raiseOnChange("childCollection");
      }
      this.parent?.raiseOnChange("childCollection");
    }
  }

  findFirst(match: (item: T) => boolean): T | undefined {
    for (let [key, item] of this.items) {
      if (match(item)) {
        return item;
      }
    }
    return undefined;
  }

  findFirstDeleted(match: (item: T) => boolean): T | undefined {
    if (!this.deletedItems) {
      return undefined;
    }

    for (let [key, item] of this.deletedItems) {
      if (match(item)) {
        return item;
      }
    }
    return undefined;
  }

  getByPropertyValue(property: string, value: string): T | undefined {
    for (let [key, item] of this.items) {
      if ((item as any)[property] == value) {
        return item;
      }
    }
    return undefined;
  }
  //////////////////////////////////////////////////

  canBeDeletedInteractive(): boolean {
    return false;
  }

  canBeEditedInteractive(): boolean {
    return false;
  }

  canBeCopiedInteractive(): boolean {
    return false;
  }

  canBePastedInteractive(copiedObjClassName: string): boolean {
    if (this.parent == state.root) {
      if (this.name == "Schaltschränke") {
        if (copiedObjClassName == "Cabinet") {
          return true;
        }
      } else if (this.name == "Etagen") {
        if (copiedObjClassName == "Floor") {
          return true;
        }
      } else if (this.name == "Szenen Gruppen") {
        if (copiedObjClassName == "SceneGroup") {
          return true;
        }
      }
    }
    return false;
  }

  async pasteItem(params: { item: BaseModel; allItems: Array<BaseModel> }): Promise<Array<BaseModel>> {
    const { item, allItems } = params;

    if (item.className == "Floor" && this.propertyId == "f") {
      return await item.executePaste({
        parent: state.floors,
        isCopyRootObj: true,
      });
    } else if (item.className == "Cabinet" && this.propertyId == "cab") {
      return await item.executePaste({
        parent: state.cabinets,
        isCopyRootObj: true,
      });
    } else if ((item.className == "Scene" || item.className == "Multiplexer") && this.propertyId == "sg") {
      return await item.executePaste({
        parent: state.sceneGroup,
        isCopyRootObj: true,
      });
    } else {
      logger.log(
        `pasteItem not implemented for className=${item.className} && propertyId==${this.propertyId} name=${this.name}`,
      );
    }

    return [];
  }

  //////////////////////////////////////////////////////
  ///////////// for paste
  mapAfterCopy() {
    if (!this.mapAfterCopyDone) {
      super.mapAfterCopy();
      const sourceItems = this.asArray();

      const newItems: T[] = [];

      sourceItems.forEach((item) => {
        item.mapAfterCopy();
        newItems.push(item);
      });

      this.replaceItems(newItems);
    }
  }

  getItemByName(name: string): T | undefined {
    let res = this.findFirst((i) => i.name == name);
    return res;
  }

  getItemByTag(tag: string): T | undefined {
    tag = tag.toLocaleLowerCase();
    let res = this.findFirst((i) => i.tag.toLocaleLowerCase() == tag);
    return res;
  }

  getDeletedItemByTag(tag: string): T | undefined {
    tag = tag.toLocaleLowerCase();
    let res = this.findFirstDeleted((i) => i.tag.toLocaleLowerCase() == tag);
    return res;
  }

  getItemByNameOrTag(nameOrTag: string): T | undefined {
    nameOrTag = nameOrTag.toLocaleLowerCase();
    let res = this.findFirst((i) => i.name.toLocaleLowerCase() == nameOrTag || i.tag.toLocaleLowerCase() == nameOrTag);
    return res;
  }

  getOrCreateItemByName(name: string): T {
    let res = this.getItemByName(name);
    if (!res) {
      res = this.createNewItem();
      if (this.parent?.className == "Root") {
        res.parent = this;
      } else {
        res.parent = this.parent;
      }

      this.add(res);

      res.name = name;
      res.generateTagFromName();
    }

    return res;
  }

  getOrCreateItemByTag(tag: string): T {
    let res = this.getItemByTag(tag);
    if (!res) {
      res = this.createNewItem();
      if (this.parent?.className == "Root") {
        res.parent = this;
      } else {
        res.parent = this.parent;
      }
      this.add(res);

      res.tag = tag;
    }

    return res;
  }

  compareWithOtherMap(otherMap: BaseModelMap<T>, otherState: State): boolean {
    let res = true;
    for (const [key, currentItem] of this.items) {
      currentItem.compareStatus = enumCompareStatus.equal;
      const otherItem = otherMap.getItemByTag(currentItem.tag);
      if (otherItem) {
        // current & other haben beide das gleiche item
        if (!currentItem.compareWithOtherObject(otherItem, otherState)) {
          this.compareStatus |= enumCompareStatus.childModified;
        }
      } else {
        if (otherState.objectRegistry.deletedIds.includes(currentItem.id)) {
          currentItem.compareStatus = enumCompareStatus.deletedInOtherProject;
          res = false;
          // item was deleted in other, correctly captured in deletedItems
        } else {
          currentItem.compareStatus = enumCompareStatus.newInCurrentProject;
          res = false;
        }
      }
    }

    for (const [key, otherItem] of otherMap.items) {
      if (this.getItemByTag(otherItem.tag)) {
        // case has already been handled in first loop
      } else {
        // const deletedItemInSelf = this.getDeletedItemByTag(otherItem.tag);
        const deletedItemInSelf = state.objectRegistry.deletedIds.includes(otherItem.id);
        if (deletedItemInSelf) {
          otherItem.compareStatus = enumCompareStatus.deletedInCurrentProject;
          this.addToDeletedItems(otherItem);
          res = false;
        } else {
          if (!this.newInOtherProjectItems) {
            this.newInOtherProjectItems = new Map();
          }
          this.newInOtherProjectItems.set(otherItem.id, otherItem);
          otherItem.compareStatus = enumCompareStatus.newInOtherProject;
          res = false;
        }
      }
    }
    return res;
  }

  consistencyCheck(parent: BaseModel) {
    this.items.forEach((item) => item.consistencyCheck(parent));
  }

  toString(): string {
    let res = "";
    this.items.forEach((item) => {
      res += `${item.tag}, `;
    });
    return res;
  }
}
